import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/components/auth/AuthContext';
import en from './en.json';
import ru from './ru.json';
import uz from './uz.json';
import { getStoredLanguage, normalizeLang, setStoredLanguage, type LangCode } from './lang';

const API_BASE = (((import.meta as any).env?.VITE_API_URL) || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/+$/, '');

type Dict = Record<string, any>;

const dictionaries: Record<LangCode, Dict> = {
  EN: en,
  RU: ru,
  UZ: uz,
};

type I18nContextValue = {
  language: LangCode;
  setLanguage: (lang: LangCode) => void;
  setUserLanguage: (lang: LangCode) => Promise<boolean>;
  t: (key: string, vars?: Record<string, string | number>) => string;
};

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

const getByPath = (obj: Dict, path: string): any => {
  return path.split('.').reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), obj);
};

const interpolate = (value: string, vars?: Record<string, string | number>) => {
  if (!vars) return value;
  return value.replace(/\{\{\s*([\w.-]+)\s*\}\}/g, (_, key) => {
    const v = vars[key];
    return v === undefined || v === null ? '' : String(v);
  });
};

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, refresh } = useAuth();
  const [language, setLanguageState] = useState<LangCode>(() => getStoredLanguage());
  const [pendingUserLang, setPendingUserLang] = useState<LangCode | null>(null);

  useEffect(() => {
    if (user?.lang) {
      const next = normalizeLang(user.lang);
      if (pendingUserLang && next !== pendingUserLang) return;
      if (pendingUserLang && next === pendingUserLang) setPendingUserLang(null);
      if (next !== language) {
        setLanguageState(next);
        setStoredLanguage(next);
      }
    }
  }, [language, pendingUserLang, user?.lang]);

  const setLanguage = (lang: LangCode) => {
    setLanguageState(lang);
    setStoredLanguage(lang);
  };

  const setUserLanguage = async (lang: LangCode) => {
    const prevLang = language;
    setPendingUserLang(lang);
    try {
      const res = await fetch(`${API_BASE}/api/profiles/me`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': lang,
        },
        credentials: 'include',
        body: JSON.stringify({ lang }),
      });
      if (!res.ok) throw new Error('Language update failed');
      let nextLang: LangCode = lang;
      try {
        const data = await res.json();
        if (data?.lang) nextLang = normalizeLang(data.lang);
      } catch { }
      setLanguageState(nextLang);
      setStoredLanguage(nextLang);
      await refresh();
      return true;
    } catch {
      setLanguageState(prevLang);
      setStoredLanguage(prevLang);
      await refresh();
      return false;
    } finally {
      setPendingUserLang(null);
    }
  };

  const t = useMemo(() => {
    return (key: string, vars?: Record<string, string | number>) => {
      const dict = dictionaries[language] || dictionaries.EN;
      const fallback = dictionaries.EN;
      const value = getByPath(dict, key);
      const resolved = value !== undefined ? value : getByPath(fallback, key);
      if (typeof resolved === 'string') return interpolate(resolved, vars);
      return key;
    };
  }, [language]);

  const value = useMemo<I18nContextValue>(() => ({ language, setLanguage, setUserLanguage, t }), [language, t]);

  return <I18nContext.Provider value={ value }> { children } </I18nContext.Provider>;
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
};
